import {
    Box,
    Button,
    TextField,
    Typography,
    List,
    ListItem,
    IconButton,
    Paper,
    Divider,
    MenuItem,
    InputAdornment,
    Chip,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import YardIcon from "@mui/icons-material/Yard";
import SpaIcon from "@mui/icons-material/Spa";
import GrassIcon from "@mui/icons-material/Grass";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { useState, useEffect } from "react";

import { useTranslate } from "../../components/Translate/TranslateContext";

import CropInfoSection from "./Crop/CropInfoSection";
import CropSuggestionDialog from "./Crop/CropSuggestionDialog";
import CropMetrics from "./Crop/CropMetrics";

import { getCropVarieties } from "../../api/userRegistration";

const soilTypes = [
    "sand",
    "loamy_sand",
    "sandy_loam",
    "loam",
    "silt_loam",
    "silt",
    "sandy_clay_loam",
    "clay_loam",
    "silty_clay_loam",
    "sandy_clay",
    "silty_clay",
    "clay",
];

export default function FarmBasics({
    farmName,
    setFarmName,
    gardens,
    setGardens,
    onNext,
    cropTypes,
}) {
    const { translate } = useTranslate();

    const [gardenForm, setGardenForm] = useState({
        garden_name: "",
        crop_type: "",
        crop_variety: "",
        soil_type: "",
        crop_metrics: "",
    });

    const [cropVarieties, setCropVarieties] = useState([]);
    const [suggestionOpen, setSuggestionOpen] = useState(false);
    const [suggestionType, setSuggestionType] = useState("crop_type");
    const [suggestionCropType, setSuggestionCropType] = useState("");
    const [suggestedValue, setSuggestedValue] = useState("");

    const updateForm = (key, value) =>
        setGardenForm((prev) => ({ ...prev, [key]: value }));

    useEffect(() => {
        if (!gardenForm.crop_type) {
            setCropVarieties([]);
            return;
        }

        const loadVarieties = async () => {
            try {
                const rows = await getCropVarieties(gardenForm.crop_type);

                setCropVarieties(
                    Array.isArray(rows?.[0]) ? rows[0] : rows || []
                );
            } catch (err) {
                console.error(err);
                setCropVarieties([]);
            }
        };

        loadVarieties();
        updateForm("crop_variety", "");
    }, [gardenForm.crop_type]);

    const addGarden = () => {
        const name = gardenForm.garden_name.trim();

        if (
            !name ||
            !gardenForm.crop_type ||
            !gardenForm.crop_variety ||
            !gardenForm.soil_type ||
            !gardenForm.crop_metrics?.length
        ) return;

        const normalizedCropTypes =
            Array.isArray(cropTypes?.[0]) ? cropTypes[0] : cropTypes || [];

        const crop = normalizedCropTypes.find(
            (t) => String(t.crop_type_id) === String(gardenForm.crop_type)
        );

        const variety = cropVarieties.find(
            (v) =>
                String(v.crop_variety_id) ===
                String(gardenForm.crop_variety)
        );

        setGardens([
            ...gardens,
            {
                garden_name: name,
                crop_type: crop?.crop_name || "",
                crop_variety: variety?.variety_name || "",
                soil_type: gardenForm.soil_type,
                crop_metrics: gardenForm.crop_metrics || [],
            },
        ]);

        setGardenForm((prev) => ({
            ...prev,
            garden_name: "",
        }));

    };

    const deleteGarden = (index) =>
        setGardens(gardens.filter((_, i) => i !== index));

    const canAddGarden =
        gardenForm.garden_name.trim() &&
        gardenForm.crop_type &&
        gardenForm.crop_variety &&
        gardenForm.soil_type;

    const canContinue =
        farmName.trim() && gardens.length > 0;

    return (
        <>
            <Box sx={{ width: "100%", maxWidth: 820, mx: "auto", py: 7, px: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mb: 5 }}>
                    <AgricultureIcon sx={{ color: "var(--color-green-main)", fontSize: 34 }} />

                    <Typography
                        variant="h3"
                        sx={{
                            fontFamily: "Fraunces",
                            color: "var(--color-text-primary)",
                            fontWeight: 300,
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {translate("farm_setup")}
                    </Typography>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        borderRadius: "28px",
                        border: "1px solid rgba(36,88,42,0.12)",
                        background: "linear-gradient(to bottom, #fffdf8 0%, #f9fcf7 100%)",
                        boxShadow: "0 10px 30px rgba(5,40,18,0.04)",
                    }}
                >
                    <Box sx={{ mb: 5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                            <YardIcon sx={{ color: "var(--color-green-main)" }} />

                            <Typography variant="h6" sx={{ fontFamily: "Fraunces" }}>
                                {translate("farm_name")}
                            </Typography>
                        </Box>

                        <TextField
                            fullWidth
                            placeholder={translate("farm_name")}
                            value={farmName}
                            onChange={(e) => setFarmName(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AgricultureIcon sx={{ color: "rgba(36,88,42,0.5)" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <Divider sx={{ my: 4, borderColor: "rgba(36,88,42,0.12)" }} />

                    <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 1 }}>
                        <SpaIcon sx={{ color: "var(--color-green-main)" }} />

                        <Typography variant="h5" sx={{ fontFamily: "Fraunces" }}>
                            {translate("add_garden")}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            p: 3,
                            borderRadius: 5,
                            backgroundColor: "rgba(212,233,207,0.22)",
                            border: "1px solid rgba(36,88,42,0.08)",
                        }}
                    >
                        <TextField
                            fullWidth
                            label={translate("garden_name")}
                            value={gardenForm.garden_name}
                            onChange={(e) =>
                                updateForm("garden_name", e.target.value)
                            }
                            sx={{ mb: 3 }}
                        />

                        <CropInfoSection
                            form={gardenForm}
                            setForm={setGardenForm}
                            cropTypes={cropTypes}
                            cropVarieties={cropVarieties}
                            translate={translate}
                            fieldSx={{ mb: 2.5 }}
                            onSuggestClick={() => setSuggestionOpen(true)}
                            errors={{}}
                        />

                        <CropMetrics
                            form={gardenForm}
                            setForm={setGardenForm}
                            cropTypes={cropTypes}
                            translate={translate}
                        />
                         {translate("soil")}
                        <TextField
                            select
                            fullWidth
                            required
                            label={translate("soil_type")}
                            value={gardenForm.soil_type}
                            onChange={(e) =>
                                updateForm("soil_type", e.target.value)
                            }
                            sx={{ mt: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <GrassIcon sx={{ color: "rgba(36,88,42,0.55)" }} />
                                    </InputAdornment>
                                ),
                            }}
                        >
                            {soilTypes.map((soil) => (
                                <MenuItem key={soil} value={soil}>
                                    {translate(soil)}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                disabled={!canAddGarden}
                                onClick={addGarden}
                                sx={{
                                    height: 48,
                                    px: 3,
                                    borderRadius: 999,
                                    backgroundColor: "var(--color-green-main)",
                                    boxShadow: "none",
                                    "&:hover": {
                                        backgroundColor: "var(--color-green-strong)",
                                        boxShadow: "none",
                                    },
                                }}
                            >
                                {translate("add_garden")}
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {!!gardens.length && (
                    <Box sx={{ mt: 5 }}>
                        <Typography variant="h5" sx={{ mb: 2.5, fontFamily: "Fraunces" }}>
                            {translate("gardens")}
                        </Typography>

                        <List sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {gardens.map((garden, index) => (
                                <ListItem
                                    key={index}
                                    sx={{
                                        borderRadius: 5,
                                        background: "#ffffff",
                                        border: "1px solid rgba(36,88,42,0.08)",
                                        boxShadow: "0 8px 24px rgba(5,40,18,0.04)",
                                        p: 3,
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 700,
                                                color: "var(--color-text-primary)",
                                            }}
                                        >
                                            {garden.garden_name}
                                        </Typography>

                                        <Box
    sx={{
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
        mt: 1.5,
    }}
>
    <Chip  sx={{
        backgroundColor: "var(--color-green-soft)",
        color: "var(--color-text-inverse)",
        borderColor: "var(--color-green-soft)",
        fontWeight: 600,
        "& .MuiChip-label": {
            px: 1.25,
        },
    }}label={garden.crop_type} size="small" />
    <Chip  sx={{
        backgroundColor: "var(--color-green-soft)",
        color: "var(--color-text-inverse)",
        borderColor: "var(--color-green-soft)",
        fontWeight: 600,
        "& .MuiChip-label": {
            px: 1.25,
        },
    }} label={garden.crop_variety} size="small" />
    <Chip  sx={{
        backgroundColor: "var(--color-green-soft)",
        color: "var(--color-text-inverse)",
        borderColor: "var(--color-green-soft)",
        fontWeight: 600,
        "& .MuiChip-label": {
            px: 1.25,
        },
    }} label={translate(garden.soil_type)} size="small" />

    {(garden.crop_metrics || []).map((metric) => (
       <Chip
    key={metric}
    label={translate(metric)}
    size="small"
    sx={{
        backgroundColor: "var(--color-green-soft)",
        color: "var(--color-text-inverse)",
        borderColor: "var(--color-green-soft)",
        fontWeight: 600,
        "& .MuiChip-label": {
            px: 1.25,
        },
    }}
/>
    ))}
</Box>
                                    </Box>

                                    <IconButton onClick={() => deleteGarden(index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    disabled={!canContinue}
                    endIcon={<ArrowForwardIcon />}
                    onClick={onNext}
                    sx={{
                        mt: 5,
                        height: 58,
                        borderRadius: 999,
                        fontSize: 16,
                    }}
                >
                    {translate("next")}
                </Button>
            </Box>

            <CropSuggestionDialog
                open={suggestionOpen}
                onClose={() => setSuggestionOpen(false)}
                translate={translate}
                suggestionType={suggestionType}
                setSuggestionType={setSuggestionType}
                suggestionCropType={suggestionCropType}
                setSuggestionCropType={setSuggestionCropType}
                suggestedValue={suggestedValue}
                setSuggestedValue={setSuggestedValue}
                cropTypes={cropTypes}
                onSubmit={() => {
                    setSuggestionOpen(false);
                    setSuggestedValue("");
                    setSuggestionCropType("");
                    setSuggestionType("crop_type");
                }}
            />
        </>
    );
}